/* agent.aixlabs.co home — one page of widgets, modeled on brain.aixlabs.co (shray-systems-brain dash-ui/app.js).
   Widgets come from widgets/registry.ts and fail on their own. Wide screens get 2–6 columns that each scroll
   and can be resized; narrow screens stack. Layout (hidden, snoozed, placement, order) syncs across devices;
   collapse state, column widths and density stay per device. Page keys: 1–9 pick view · [ ] prev/next view ·
   h/l prev/next widget · / filter · r refresh. Views (tabs) are hardcoded in viewRegistry.ts. Row keys (j/k, Enter, o, actions, d, c) belong to the active widget: components/WidgetSection.tsx.
   Building a widget → brain/aix-command-center/architecture/home-widgets.md */
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { Building2, LayoutGrid, RefreshCw } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { PointerEvent as ReactPointerEvent } from "react"

import { trpc } from "../../lib/trpc"
import "./homeDashboard.css"
import { ViewTabs } from "./components/ViewTabs"
import { WidgetSection } from "./components/WidgetSection"
import type { EDensity, IHomeDashboardLayout, IWidgetDefinition, IWidgetSummary } from "./homeDashboard.types"
import {
  DENSITY_OPTIONS,
  EMove,
  layoutOf,
  withLayout,
  MAX_COLUMNS,
  columnCountFor,
  columnOf,
  densityOf,
  hasModifier,
  isSnoozed,
  isTyping,
  moveWidget,
  placeWidgets,
  setWidgetShown,
  snoozeWidget,
  stepIndex,
  widgetInCompanies,
} from "./homeDashboardModel"
import { useCompanyFilter } from "./useCompanyFilter"
import { useDevicePref, useHomePrefs } from "./useHomePrefs"
import { HOME_VIEW_ID, VIEWS, viewFor, widgetsForView } from "./viewRegistry"
import { WIDGETS } from "./widgets/registry"

const MINUTE_MS = 60_000
const FLASH_MS = 900
const MIN_COLUMN_RESIZE_PX = 260

const MORNING_END_HOUR = 12
const AFTERNOON_END_HOUR = 18
/** This page's rail (200px; the app sidebar peeks over the page here): the grid width before the first ResizeObserver tick */
const CHROME_PX = 200

const greeting = (hour: number) => {
  if (hour < MORNING_END_HOUR) return "Good morning"
  if (hour < AFTERNOON_END_HOUR) return "Good afternoon"
  return "Good evening"
}

function NavCount({ summary }: { summary: IWidgetSummary | undefined }) {
  if (!summary) return <span className="hd-nav-count" />
  if (summary.error) return <span className="hd-nav-count hd-act">!</span>
  if (summary.act) return <span className="hd-nav-count hd-act">{summary.act}</span>
  if (summary.watch) return <span className="hd-nav-count hd-watch">{summary.watch}</span>
  return <span className="hd-nav-count" />
}

function HomeDashboardScreen() {
  const utils = trpc.useUtils()
  const navigate = useNavigate()
  const { focus, view } = useSearch({ strict: false }) as { focus?: string; view?: string }
  const [allPrefs, updateAllPrefs] = useHomePrefs()
  const [lastView, setLastView] = useDevicePref<string>("home.view.v1", HOME_VIEW_ID)
  const activeView = viewFor(view ?? lastView)
  const viewId = activeView.id
  // Only the widgets this view lists (viewRegistry.ts); hide/snooze/move apply within it
  const viewWidgets = useMemo(() => widgetsForView(WIDGETS, activeView), [activeView])
  // Everything below reads and edits the active view's layout; Home is the top-level one
  const prefs = useMemo(() => layoutOf(allPrefs, viewId), [allPrefs, viewId])
  const updatePrefs = useCallback(
    (fn: (prev: IHomeDashboardLayout) => IHomeDashboardLayout) =>
      updateAllPrefs((prev) => withLayout(prev, viewId, fn(layoutOf(prev, viewId)))),
    [updateAllPrefs, viewId],
  )
  const [collapsed, setCollapsed] = useDevicePref<string[]>("home.collapsed.v1", [])
  const [columnOverride, setColumnOverride] = useDevicePref<number>("home.columns.v1", 0)
  const [colWidths, setColWidths] = useDevicePref<Record<string, number[]>>("home.colw.v1", {})
  const [densities, setDensities] = useDevicePref<Record<string, EDensity>>("home.density.v1", {})
  const [filter, setFilter] = useState("")
  const [now, setNow] = useState(() => Date.now())
  const [width, setWidth] = useState(() => window.innerWidth - CHROME_PX)
  const [summaries, setSummaries] = useState<Record<string, IWidgetSummary>>({})
  const [current, setCurrent] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)

  const n = columnCountFor(width, columnOverride)
  const companyFilter = useCompanyFilter(activeView.company)
  const { directory, selected: selectedCompanies, setSelected: setSelectedCompanies } = companyFilter
  // Widgets about one product (Mission, Bets) drop out when the company filter excludes it
  const columns = useMemo(
    () =>
      placeWidgets(viewWidgets, prefs, n, now).map((col) =>
        col.filter((def) => widgetInCompanies(def, selectedCompanies)),
      ),
    [viewWidgets, prefs, n, now, selectedCompanies],
  )
  const companyLabel = selectedCompanies.length
    ? selectedCompanies.map((slug) => directory.find((c) => c.slug === slug)?.name ?? slug).join(", ")
    : "All companies"
  const toggleCompany = (slug: string, on: boolean) =>
    setSelectedCompanies(on ? [...selectedCompanies, slug] : selectedCompanies.filter((s) => s !== slug))
  const visible = useMemo(() => columns.flat(), [columns])
  const emptyMessage = (() => {
    if (activeView.unassigned && !viewWidgets.length)
      return "Nothing experimental right now. Widgets that no other view lists show up here."
    if (selectedCompanies.length) return `No widgets for ${companyLabel}.`
    return "Every widget is hidden or snoozed. Add some back from Widgets."
  })()
  const snoozed = viewWidgets.filter((def) => isSnoozed(prefs, def.id, now))
  const query = filter.trim().toLowerCase()

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), MINUTE_MS)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const onSummary = useCallback((id: string, summary: IWidgetSummary) => {
    setSummaries((prev) => ({ ...prev, [id]: summary }))
  }, [])
  const onToggleCollapse = useCallback(
    (id: string) => setCollapsed(collapsed.includes(id) ? collapsed.filter((x) => x !== id) : [...collapsed, id]),
    [collapsed, setCollapsed],
  )
  const onSnooze = useCallback(
    (id: string, days: number) => updatePrefs((prev) => snoozeWidget(prev, id, days, Date.now())),
    [updatePrefs],
  )
  const onHide = useCallback(
    (id: string) => updatePrefs((prev) => setWidgetShown(viewWidgets, prev, id, false)),
    [updatePrefs, viewWidgets],
  )
  const onDensity = useCallback(
    (id: string, density: EDensity) => setDensities({ ...densities, [id]: density }),
    [densities, setDensities],
  )
  const setAllDensities = (density: EDensity) =>
    setDensities(Object.fromEntries(WIDGETS.map((def) => [def.id, density])))
  const sharedDensity = (() => {
    const all = new Set(WIDGETS.map((def) => densityOf(def, densities)))
    return all.size === 1 ? [...all][0] : ""
  })()
  const onMove = useCallback(
    (id: string, move: EMove) => updatePrefs((prev) => moveWidget(viewWidgets, prev, n, id, move, Date.now())),
    [updatePrefs, n, viewWidgets],
  )

  const jump = useCallback((id: string) => {
    document.getElementById(`hd-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })
    setCurrent(id)
    setFlash(id)
    setTimeout(() => setFlash((f) => (f === id ? null : f)), FLASH_MS)
  }, [])

  // Deep links (e.g. /my-work → /?focus=my-work) open scrolled to that widget
  useEffect(() => {
    if (focus) jump(focus)
  }, [focus, jump])

  const refreshAll = useCallback(() => void utils.invalidate(), [utils])

  // `?view=` makes a view linkable; the last one picked on this device opens by default
  const selectView = useCallback(
    (id: string) => {
      setLastView(id)
      // Keep other params, but drop ?company= so the new view's default company applies
      void navigate({
        to: "/",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          view: id === HOME_VIEW_ID ? undefined : id,
          company: undefined,
        }),
      })
    },
    [navigate, setLastView],
  )
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && document.activeElement === filterRef.current) {
        setFilter("")
        filterRef.current?.blur()
        return
      }
      if (isTyping(e.target) || hasModifier(e)) return
      if (/^[1-9]$/.test(e.key)) {
        // Digits are reserved for views (tab order); widgets move with h/l
        const target = VIEWS[Number(e.key) - 1]
        if (target) {
          e.preventDefault()
          if (target.id !== viewId) selectView(target.id)
        }
      } else if (e.key === "h" || e.key === "l" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const i = visible.findIndex((def) => def.id === current)
        const next = visible[stepIndex(visible.length, i, e.key === "l" || e.key === "ArrowRight" ? 1 : -1)]
        if (next) jump(next.id)
      } else if ((e.key === "j" || e.key === "k") && !visible.some((def) => def.id === current)) {
        // Rows belong to the active widget; with none active yet, the first j/k picks the first widget
        const first = visible[0]
        if (first) setCurrent(first.id)
      } else if (e.key === "/") {
        e.preventDefault()
        filterRef.current?.focus()
      } else if (e.key === "r") {
        refreshAll()
      } else if (e.key === "[" || e.key === "]") {
        const ids = VIEWS.map((v) => v.id)
        const next = ids[(ids.indexOf(viewId) + (e.key === "]" ? 1 : -1) + ids.length) % ids.length]
        if (next && next !== viewId) selectView(next)
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [visible, current, jump, refreshAll, viewId, selectView])

  // Column resize: drag the gap between two columns; double-click resets. Widths are fractions per column count.
  const fractions = colWidths[String(n)]?.length === n ? colWidths[String(n)] : Array.from({ length: n }, () => 1)
  const startResize = (i: number, e: ReactPointerEvent<HTMLDivElement>) => {
    const grid = gridRef.current
    if (!grid) return
    e.preventDefault()
    const cols = [...grid.querySelectorAll<HTMLElement>(":scope > .hd-colwrap")]
    const px = cols.map((col) => col.getBoundingClientRect().width)
    const left = px[i - 1] ?? 0
    const pair = left + (px[i] ?? 0)
    const x0 = e.clientX
    const total = px.reduce((sum, v) => sum + v, 0)
    const move = (ev: PointerEvent) => {
      const a = Math.max(MIN_COLUMN_RESIZE_PX, Math.min(pair - MIN_COLUMN_RESIZE_PX, left + ev.clientX - x0))
      const next = px.slice()
      next[i - 1] = a
      next[i] = pair - a
      setColWidths({ ...colWidths, [String(n)]: next.map((v) => Math.round((v / total) * 1000) / 100) })
    }
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }
  const resetWidths = () => {
    const next = { ...colWidths }
    delete next[String(n)]
    setColWidths(next)
  }

  const canMove = (def: IWidgetDefinition): Record<EMove, boolean> => {
    const col = columnOf(def, prefs, n)
    const siblings = columns[col - 1] ?? []
    const i = siblings.indexOf(def)
    return {
      [EMove.LEFT]: col > 1,
      [EMove.RIGHT]: col < n,
      [EMove.UP]: i > 0,
      [EMove.DOWN]: i >= 0 && i < siblings.length - 1,
    }
  }

  const hour = new Date(now).getHours()
  const stacked = n === 1

  return (
    <div className="home-dash">
      <aside className="hd-rail" aria-label="Widgets">
        <div className="hd-rail-head">
          <p className="hd-mark">AIX</p>
          <p className="hd-muted">{greeting(hour)}</p>
          <p className="hd-muted">
            {new Date(now).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
          </p>
        </div>
        <nav className="hd-nav">
          {visible.map((def) => (
            <button
              key={def.id}
              type="button"
              className="hd-nav-item"
              aria-current={current === def.id}
              onClick={() => jump(def.id)}
            >
              <span className="hd-nav-name">{def.title}</span>
              <NavCount summary={summaries[def.id]} />
            </button>
          ))}
        </nav>
        <dl className="hd-keys" aria-label="Keyboard shortcuts">
          <div>
            <dt>1–9</dt>
            <dd>switch view</dd>
          </div>
          <div>
            <dt>h l</dt>
            <dd>previous, next widget</dd>
          </div>
          <div>
            <dt>j k</dt>
            <dd>rows in widget</dd>
          </div>
          <div>
            <dt>↵ o</dt>
            <dd>expand, open link</dd>
          </div>
          <div>
            <dt>d c</dt>
            <dd>density, collapse</dd>
          </div>
          <div>
            <dt>/</dt>
            <dd>filter rows</dd>
          </div>
          <div>
            <dt>r</dt>
            <dd>refresh all</dd>
          </div>
          <div>
            <dt>[ ]</dt>
            <dd>previous, next view</dd>
          </div>
        </dl>
      </aside>

      <main className="hd-page">
        <ViewTabs activeId={viewId} onSelect={selectView} />
        <div className="hd-toolbar">
          <label className="hd-filter">
            <span className="sr-only">Filter rows</span>
            <input
              ref={filterRef}
              type="search"
              placeholder="Filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <kbd aria-hidden="true">/</kbd>
          </label>
          <button type="button" className="hd-quiet-btn" onClick={refreshAll}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`hd-quiet-btn${selectedCompanies.length ? " hd-on" : ""}`}
                aria-label="Filter by company"
              >
                <Building2 className="h-3.5 w-3.5" /> {companyLabel}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              <DropdownMenuLabel>Companies and products</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={!selectedCompanies.length}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => setSelectedCompanies([])}
              >
                All companies
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              {directory.map((company) => (
                <DropdownMenuCheckboxItem
                  key={company.slug}
                  checked={selectedCompanies.includes(company.slug)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(on) => toggleCompany(company.slug, on === true)}
                >
                  <span className="hd-company-dot" style={{ background: company.color ?? undefined }} />
                  {company.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="hd-quiet-btn">
                <LayoutGrid className="h-3.5 w-3.5" /> Widgets
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>In {activeView.name}</DropdownMenuLabel>
              {viewWidgets.map((def) => (
                <DropdownMenuCheckboxItem
                  key={def.id}
                  checked={!prefs.hidden.includes(def.id)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(on) =>
                    updatePrefs((prev) => setWidgetShown(viewWidgets, prev, def.id, on === true))
                  }
                >
                  <span className="flex flex-col">
                    <span>{def.title}</span>
                    <span className="text-xs text-muted-foreground">{def.description}</span>
                  </span>
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Columns on this device</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={String(columnOverride)}
                onValueChange={(v) => setColumnOverride(Number(v))}
              >
                <DropdownMenuRadioItem value="0">Auto ({columnCountFor(width, 0)})</DropdownMenuRadioItem>
                {Array.from({ length: MAX_COLUMNS }, (_, i) => (
                  <DropdownMenuRadioItem key={i + 1} value={String(i + 1)}>
                    {i + 1} column{i ? "s" : ""}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Density for every widget on this device</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={sharedDensity} onValueChange={(v) => setAllDensities(v as EDensity)}>
                {DENSITY_OPTIONS.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          {snoozed.length > 0 && (
            <div className="hd-snoozed">
              <span className="hd-muted">Snoozed</span>
              {snoozed.map((def) => (
                <span key={def.id} className="hd-snoozed-item">
                  {def.title}
                  <span className="hd-muted">
                    until{" "}
                    {new Date(prefs.snoozes[def.id] ?? now).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <button type="button" className="hd-quiet-btn" onClick={() => onSnooze(def.id, 0)}>
                    Wake
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div
          ref={gridRef}
          className={`hd-grid${stacked ? " stacked" : ""}`}
          style={stacked ? undefined : { gridTemplateColumns: fractions.map((f) => `minmax(0, ${f}fr)`).join(" ") }}
        >
          {!visible.length && <p className="hd-state hd-empty-view">{emptyMessage}</p>}
          {visible.length > 0 &&
            columns.map((col, c) => (
              <div key={c} className="hd-colwrap" data-col={c + 1}>
                {c > 0 && !stacked && (
                  <div
                    className="hd-resize"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize columns (double-click to reset)"
                    onPointerDown={(e) => startResize(c, e)}
                    onDoubleClick={resetWidths}
                  />
                )}
                <div className="hd-col">
                  {col.map((def) => (
                    <WidgetSection
                      key={def.id}
                      def={def}
                      collapsed={collapsed.includes(def.id)}
                      filter={query}
                      flash={flash === def.id}
                      active={current === def.id}
                      density={densityOf(def, densities)}
                      companyFilter={companyFilter}
                      canMove={canMove(def)}
                      now={now}
                      onActivate={setCurrent}
                      onToggleCollapse={onToggleCollapse}
                      onDensity={onDensity}
                      onSnooze={onSnooze}
                      onHide={onHide}
                      onMove={onMove}
                      onSummary={onSummary}
                    />
                  ))}
                  {!col.length && (
                    <p className="hd-state hd-empty-col">Empty column — move a widget here from its ⋯ menu.</p>
                  )}
                </div>
              </div>
            ))}
        </div>
      </main>
    </div>
  )
}

export default HomeDashboardScreen
